import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getLangGraphWsUrl } from '../../utils/langgraphConfig.js';

function readStudioMcp(d) {
  const meta = d.metadata && typeof d.metadata === 'object' ? d.metadata : {};
  const studio = meta.studio && typeof meta.studio === 'object' ? meta.studio : {};
  const mcp = studio.mcpContext;
  return {
    meta,
    studio,
    mcp: mcp != null && typeof mcp === 'object' && !Array.isArray(mcp) ? mcp : {},
  };
}

const defaultWsUrl = () => {
  if (typeof window === 'undefined') return '';
  return getLangGraphWsUrl('/api/v1/mcp/ws');
};

const MCP_PRESETS = {
  minimal: {
    clientName: 'studio-v2',
    capabilities: { tools: true },
  },
  agentTools: {
    clientName: 'studio-v2-agent',
    capabilities: { tools: true, resources: true, prompts: true },
    defaultToolTimeoutMs: 30000,
  },
};

/**
 * MCP context forwarded to Python as `flow.mcpContext` (see `v2Execution.adapter.js`).
 * Stored under `definition.metadata.studio.mcpContext`.
 */
export default function StudioV2FlowMcpEditor({ text, setText }) {
  const [local, setLocal] = useState('{}');
  const [serverUrl, setServerUrl] = useState('');
  const [error, setError] = useState(null);
  const [connectionState, setConnectionState] = useState({ testing: false, ok: null, detail: '' });

  useEffect(() => {
    try {
      const d = JSON.parse(text || '{}');
      const { mcp } = readStudioMcp(d);
      setLocal(JSON.stringify(mcp, null, 2));
      setServerUrl(typeof mcp.serverUrl === 'string' ? mcp.serverUrl : '');
      setError(null);
    } catch (e) {
      setError(e.message || 'Invalid JSON');
    }
  }, [text]);

  const writeMcp = (mcpObj) => {
    if (mcpObj === null || typeof mcpObj !== 'object' || Array.isArray(mcpObj)) {
      setError('MCP context must be a JSON object.');
      return;
    }
    try {
      const d = JSON.parse(text || '{}');
      const { meta } = readStudioMcp(d);
      const studio = meta.studio && typeof meta.studio === 'object' ? { ...meta.studio } : {};
      studio.mcpContext = mcpObj;
      const nextMeta = { ...meta, studio };
      const next = { ...d, metadata: nextMeta };
      setText(JSON.stringify(next, null, 2));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const onBlurJsonCommit = () => {
    let parsed;
    try {
      parsed = JSON.parse(local.trim() || '{}');
    } catch (e) {
      setError(e.message);
      return;
    }
    const merged = { ...parsed };
    const u = serverUrl.trim();
    if (u) merged.serverUrl = u;
    else delete merged.serverUrl;
    writeMcp(merged);
  };

  const onBlurServerUrl = () => {
    let parsed;
    try {
      parsed = JSON.parse(local.trim() || '{}');
    } catch (e) {
      setError(e.message);
      return;
    }
    const merged = { ...parsed };
    const u = serverUrl.trim();
    if (u) merged.serverUrl = u;
    else delete merged.serverUrl;
    setLocal(JSON.stringify(merged, null, 2));
    writeMcp(merged);
  };

  const testMcpConnection = () => {
    const target = (serverUrl || defaultWsUrl()).trim();
    if (!target) {
      setConnectionState({ testing: false, ok: false, detail: 'Provide an MCP WebSocket URL first.' });
      return;
    }
    if (!/^wss?:\/\//i.test(target)) {
      setConnectionState({ testing: false, ok: false, detail: 'MCP URL must start with ws:// or wss://.' });
      return;
    }
    setConnectionState({ testing: true, ok: null, detail: '' });
    let ws;
    let timeout;
    let done = false;
    try {
      ws = new WebSocket(target);
    } catch (e) {
      setConnectionState({ testing: false, ok: false, detail: e?.message || 'Could not create WebSocket connection.' });
      return;
    }
    const finish = (ok, detail) => {
      if (done) return;
      done = true;
      clearTimeout(timeout);
      try { ws.close(); } catch (_) { /* ignore */ }
      setConnectionState({ testing: false, ok, detail });
    };
    timeout = setTimeout(() => finish(false, 'Connection timeout (5s). Check URL/network and server health.'), 5000);
    ws.onopen = () => finish(true, `Connected to ${target}`);
    ws.onerror = () => finish(false, `Failed to connect to ${target}`);
    ws.onclose = (ev) => {
      finish(false, `Connection closed before handshake completed (code ${ev.code || 'n/a'}).`);
    };
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>MCP context</Typography>
        <Typography variant="caption" color="text.secondary">
          Forwarded as Python
          {' '}
          <code>mcpContext</code>
          . Use
          {' '}
          <code>serverUrl</code>
          {' '}
          for the default MCP WebSocket (same field as legacy MCP tool nodes). Additional keys stay in JSON below.
        </Typography>
        <Alert severity="info" sx={{ py: 0.75 }}>
          <Typography variant="body2">
            Manage MCP servers, tools, and test connections in the
            {' '}
            <Box component={RouterLink} to="/mcp-dashboard" sx={{ fontWeight: 600, textDecoration: 'underline' }}>MCP dashboard</Box>
            . The legacy flow builder embeds a richer
            {' '}
            <code>MCPFlowIntegration</code>
            {' '}
            sidebar; Studio keeps flow-level context here until that UI is ported.
          </Typography>
        </Alert>
        <TextField
          label="MCP WebSocket URL"
          size="small"
          fullWidth
          value={serverUrl}
          onChange={(e) => setServerUrl(e.target.value)}
          onBlur={onBlurServerUrl}
          placeholder={defaultWsUrl()}
          helperText="Leave empty to omit serverUrl from the object; tools may still set per-node URLs."
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="contained"
            onClick={testMcpConnection}
            disabled={connectionState.testing}
          >
            {connectionState.testing ? 'Testing connection…' : 'Test MCP connection'}
          </Button>
          {connectionState.testing && <CircularProgress size={18} />}
        </Stack>
        {connectionState.ok === true && <Alert severity="success">{connectionState.detail}</Alert>}
        {connectionState.ok === false && <Alert severity="warning">{connectionState.detail}</Alert>}
        <Divider />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const next = { ...MCP_PRESETS.minimal, ...(serverUrl.trim() ? { serverUrl: serverUrl.trim() } : {}) };
              setLocal(JSON.stringify(next, null, 2));
              writeMcp(next);
            }}
          >
            Apply minimal preset
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const next = { ...MCP_PRESETS.agentTools, ...(serverUrl.trim() ? { serverUrl: serverUrl.trim() } : {}) };
              setLocal(JSON.stringify(next, null, 2));
              writeMcp(next);
            }}
          >
            Apply agent-tools preset
          </Button>
        </Stack>
        <Divider />
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Full MCP context (JSON object)"
          multiline
          minRows={10}
          fullWidth
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={onBlurJsonCommit}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
        />
      </Stack>
    </Paper>
  );
}
